import type { IScrollMapInfoRow } from '@gem-sdk/heatmap-react';

import {
  buildBuckets,
  EScrollType,
  useHeatmapDataContext,
  useHeatmapSettingContext,
} from '@gem-sdk/heatmap-react';
import { useHeatmapLoadingContext, useHeatmapScrollConfig } from '~/modules/heatmap/hooks';
import { BlockStack, Box, InlineGrid, Text } from '@shopify/polaris';
import { EHeatmapLoadingKey } from '~/modules/heatmap/types';
import { ScrollRankSkeleton } from './ScrollRankSkeleton';
import { ScrollRankItem } from './ScrollRankItem';
import { GTooltipCard } from '~/presentation/ui';
import { SPACING_INSET } from '../Sidebar';
import { useMemo } from 'react';

export interface IScrollData extends IScrollMapInfoRow {
  startY: number;
  endY: number;
}

interface ScrollRankProps {
  isScrolled: boolean;
}

export const ScrollRank: React.FC<ScrollRankProps> = () => {
  const scrollType = useHeatmapSettingContext((s) => s.scrollType);
  const scrollMapInfo = useHeatmapDataContext((s) => s.dataInfo?.scrollMapInfo);
  const isLoading = useHeatmapLoadingContext((s) =>
    s.getLoadingByKey(EHeatmapLoadingKey.HEATMAP_SCROLL),
  );

  const { data: columns, ui } = useHeatmapScrollConfig();

  const scrollData = useMemo(() => {
    if (!scrollMapInfo?.rows) return [];
    const rows = Object.values(scrollMapInfo.rows) as IScrollMapInfoRow[];

    return scrollType === EScrollType.Depth
      ? buildDepthScrollData(rows)
      : buildRangeScrollData(rows);
  }, [scrollMapInfo, scrollType]);

  if (isLoading) return <ScrollRankSkeleton columns={columns} />;
  // if (!scrollMapInfo || scrollData.length === 0) return <ScrollRankEmpty />;

  return (
    <Box padding={SPACING_INSET} paddingBlockStart="0" paddingInlineEnd="0">
      <Box position="sticky" insetBlockStart="0" zIndex="10" background="bg-fill">
        <Box paddingInline="150" paddingBlock="500">
          <InlineGrid columns={ui.columns} gap="200" alignItems="center">
            {columns.map((col) => (
              <GTooltipCard
                key={col.label}
                wrapper="span"
                alignment={col.labelAlignment ?? 'start'}
                tooltip={col.tooltip}
                isHideBorder
                textDecoration="underline"
                preferredAlignment="center"
              >
                <Text
                  as="span"
                  variant="bodySm"
                  fontWeight="medium"
                  alignment={col.labelAlignment ?? 'start'}
                  key={col.label}
                >
                  {col.label}
                </Text>
              </GTooltipCard>
            ))}
          </InlineGrid>
        </Box>
        <div className="gx-hm-divider" />
      </Box>

      <BlockStack gap="0">
        {scrollData.map((item, index) => (
          <ScrollRankItem key={item.startY + item.endY + index} item={item} columns={columns} />
        ))}
      </BlockStack>
    </Box>
  );
};

/**
 * Depth: data 0-99 (100 values).
 * UI shows multiples of 5 excluding 0 → 5, 10, ..., 95 + last item as 100 (20 items).
 * startY = endY = scrollReachPercent (single point, not a range).
 */
export const buildDepthScrollData = (rows: IScrollMapInfoRow[]): IScrollData[] => {
  const sorted = [...rows].sort((a, b) => a.scrollReachPercent - b.scrollReachPercent);
  const last = sorted.at(-1);

  const filtered = sorted.filter(
    (row) => row.scrollReachPercent % 5 === 0 && row.scrollReachPercent > 0,
  );
  const all = last && !filtered.includes(last) ? [...filtered, last] : filtered;

  return all.map((row, i) => ({
    ...row,
    startY: row.scrollReachPercent,
    endY: i === all.length - 1 ? 100 : row.scrollReachPercent,
  }));
};

/**
 * Attention / Revenue: data step 5 (20 values, position = bucket start).
 * UI shows ranges: 0→5, 5→10, ..., 95→100 (20 items).
 */
export const buildRangeScrollData = (rows: IScrollMapInfoRow[]): IScrollData[] =>
  buildBuckets(rows, (r) => r.scrollReachPercent);
